import { __awaiter } from "tslib";
import { v4 as uuidv4 } from "uuid";
import { AveApiError } from "../external-api/ave-api";
import ChunkHandler from "./chunk-handler";
import { AveMessageType, AveResponseEvents } from "@org-avp/ave-message-factory";
import { beautifyObject, log } from "../utils";
const dataChannelResponseTimeoutInMs = 30000;
export default class DatachannelHandler {
    constructor(eventEmitter, chunkHandler) {
        this.eventEmitter = eventEmitter;
        this.chunkHandler = chunkHandler;
    }
    handleAveResponse(datachannelResponse) {
        var _a, _b, _c, _d;
        this.validateAveDataChannelResponse(datachannelResponse);
        const response = datachannelResponse[0];
        if (response.meta.type === AveMessageType.ERROR) {
            this.eventEmitter.emit(response.meta.referenceMessageId, new AveApiError(response.data.errorCode, response.data.details));
            return;
        }
        if (this.isValidInteractionSpotUpdate(response)) {
            (_a = this.interactionSpotsHandler) === null || _a === void 0 ? void 0 : _a.call(this, response.data.interactionSpots);
            return;
        }
        if (this.isValidAnimationUpdate(response)) {
            (_b = this.animationUpdateHandler) === null || _b === void 0 ? void 0 : _b.call(this, response.data.animationId, response.data.state);
            return;
        }
        if (this.isValidDrivingSceneUpdate(response)) {
            (_c = this.drivingSceneFinishedHandler) === null || _c === void 0 ? void 0 : _c.call(this);
            return;
        }
        if (ChunkHandler.isChunkedResponse(response)) {
            this.chunkHandler.handleChunkedResponse(response);
            return;
        }
        if (((_d = response.meta) === null || _d === void 0 ? void 0 : _d.referenceMessageId) && response.meta.referenceMessageId !== "none") {
            this.eventEmitter.emit(response.meta.referenceMessageId, response);
        }
    }
    registerDrivingSceneFinishedHandler(handler) {
        this.drivingSceneFinishedHandler = handler;
    }
    registerInteractionSpotsHandler(handler) {
        this.interactionSpotsHandler = handler;
    }
    deregisterInteractionSpotsHandler() {
        this.interactionSpotsHandler = undefined;
    }
    registerAnimationUpdateHandler(handler) {
        this.animationUpdateHandler = handler;
    }
    deregisterAnimationUpdateHandler() {
        this.animationUpdateHandler = undefined;
    }
    /**
     * Sends immediately an AVE message without queuing it.
     * This method provides no response and sending order guarantee.
     */
    sendFireAndForgetMessage(message) {
        message.meta.messageId = this.generateMessageId();
        this.send(message);
    }
    /**
     * Sends an AVE message using the internal queue system of the jetstream.
     * It guarantees sending order and provides retry mechanism if the data channel is temporary not available.
     */
    sendMessage(message_1) {
        return __awaiter(this, arguments, void 0, function* (message, skipTimeout = false) {
            message.meta.messageId = this.generateMessageId();
            const messageId = message.meta.messageId;
            log.debug(`Sending data channel message to AVE: ${beautifyObject(message)}`);
            this.send(message);
            // Return AVE response once received
            return new Promise((resolve, reject) => {
                // if no response is received in a predefined time, then we assume something went wrong and the listener is removed
                // since larger payloads can take longer to receive (e.g. large images) we allow skipping the timeout
                let timeoutId;
                if (!skipTimeout) {
                    timeoutId = setTimeout(() => {
                        reject(new Error(`Request to AVE has timed out after ${dataChannelResponseTimeoutInMs} ms`));
                        this.eventEmitter.removeListener(messageId, listener);
                    }, dataChannelResponseTimeoutInMs);
                }
                const listener = (response) => {
                    clearTimeout(timeoutId);
                    if (response instanceof Error) {
                        reject(response.message);
                        log.debug(`Received error response by AVE: ${response.message}`);
                        return;
                    }
                    log.debug(`Received data channel response from AVE: ${beautifyObject(response)}`);
                    resolve(response);
                };
                this.eventEmitter.once(messageId, listener);
            });
        });
    }
    isValidDrivingSceneUpdate(aveMessage) {
        return aveMessage.meta.name === AveResponseEvents.AVEDrivingSceneFinished;
    }
    isValidInteractionSpotUpdate(aveMessage) {
        return aveMessage.data.interactionSpots && aveMessage.meta.name === AveResponseEvents.AVEInteractionSpots;
    }
    isValidAnimationUpdate(aveMessage) {
        // TODO: Better would be, if correct name provided by AVE
        // return aveMessage.data.animationId && aveMessage.meta.name === AveResponseEvents.AVEAnimationUpdate;
        return (aveMessage.data.animationId &&
            (aveMessage.meta.name === AveResponseEvents.AVEAnimationUpdate || aveMessage.meta.name === ""));
    }
    validateAveDataChannelResponse(response) {
        if (!response) {
            throw new Error("Response is empty where it should not be.");
        }
        if (!Array.isArray(response)) {
            throw new Error("Unexpected response format.");
        }
        if (response.length > 1) {
            log.warn(`AVE response array contains more than one entry ${response}`);
        }
        if (!response[0] || !response[0].meta || !response[0].data) {
            throw new Error("Unexpected response format.");
        }
    }
    // This overwrites the uuid generated by the AVE API library.
    // We use it for our tests to mock the generated uuid
    generateMessageId() {
        return uuidv4();
    }
    send(message) {
        if (!this.webrtcPlayer) {
            log.error("WebRTC player is not set up correctly. Cannot send message to AVE.");
            return;
        }
        this.webrtcPlayer.sendAveRequest(message);
    }
}
//# sourceMappingURL=datachannel-handler.js.map